import React, { useState, useEffect } from 'react';
import appointmentService from '../services/appointmentService';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;
const getHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const TYPE_OPTIONS = [
  { value: 'firma', label: 'Firma' },
  { value: 'consulta', label: 'Consulta' },
  { value: 'entrega', label: 'Entrega' },
  { value: 'seguimiento', label: 'Seguimiento' },
  { value: 'interno', label: 'Interno' },
];

const LOCATION_OPTIONS = [
  { value: 'oficina', label: 'Oficina' },
  { value: 'domicilio_cliente', label: 'Domicilio del cliente' },
  { value: 'otro', label: 'Otro' },
];

function toLocalDatetimeValue(datetimeStr) {
  if (!datetimeStr) return '';
  const d = new Date(datetimeStr);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AppointmentModal({ appointment, preselectedDate, onClose, onSaved, prefillClientId, prefillCaseFileId }) {
  const isEditing = !!appointment;

  const defaultStart = preselectedDate ? toLocalDatetimeValue(preselectedDate.toISOString()) : '';
  const defaultEnd = preselectedDate ? (() => {
    const e = new Date(preselectedDate); e.setHours(e.getHours() + 1);
    return toLocalDatetimeValue(e.toISOString());
  })() : '';

  const [form, setForm] = useState({
    title: '',
    type: 'consulta',
    status: 'programada',
    start_datetime: defaultStart,
    end_datetime: defaultEnd,
    location: 'oficina',
    user_id: '',
    client_id: prefillClientId || '',
    case_file_id: prefillCaseFileId || '',
    notes: '',
  });

  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [caseFiles, setCaseFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Cargar datos iniciales
  useEffect(() => {
    axios.get(`${API}/users`, getHeaders()).then(r => setUsers(r.data.data || r.data)).catch(() => {});
    axios.get(`${API}/clients?per_page=200`, getHeaders()).then(r => setClients(r.data.data || r.data)).catch(() => {});
  }, []);

  // Si hay cliente seleccionado, cargar sus expedientes
  useEffect(() => {
    if (form.client_id) {
      axios.get(`${API}/case-files?client_id=${form.client_id}&per_page=100`, getHeaders())
        .then(r => setCaseFiles(r.data.data || r.data))
        .catch(() => setCaseFiles([]));
    } else {
      setCaseFiles([]);
      setForm(f => ({ ...f, case_file_id: '' }));
    }
  }, [form.client_id]);

  // Si es edición, llenar el form
  useEffect(() => {
    if (appointment) {
      setForm({
        title: appointment.title || '',
        type: appointment.type || 'consulta',
        status: appointment.status || 'programada',
        start_datetime: toLocalDatetimeValue(appointment.start_datetime),
        end_datetime: toLocalDatetimeValue(appointment.end_datetime),
        location: appointment.location || 'oficina',
        user_id: appointment.user_id || '',
        client_id: appointment.client_id || '',
        case_file_id: appointment.case_file_id || '',
        notes: appointment.notes || '',
      });
    }
  }, [appointment]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => {
      const updated = { ...f, [name]: value };

      // Si cambia el inicio, ajustar fin automáticamente a +1 hora
      if (name === 'start_datetime' && value) {
        const start = new Date(value);
        if (!isNaN(start)) {
          const end = new Date(start);
          end.setHours(end.getHours() + 1);
          const pad = n => String(n).padStart(2, '0');
          updated.end_datetime = `${end.getFullYear()}-${pad(end.getMonth()+1)}-${pad(end.getDate())}T${pad(end.getHours())}:${pad(end.getMinutes())}`;
        }
      }

      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (isEditing) {
        await appointmentService.update(appointment.id, form);
      } else {
        await appointmentService.create(form);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar la cita');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{isEditing ? 'Editar cita' : 'Nueva cita'}</h5>
            <button className="close" onClick={onClose}><span>&times;</span></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && <div className="alert alert-danger">{error}</div>}

              {/* Título */}
              <div className="form-group">
                <label>Título <span className="text-danger">*</span></label>
                <input className="form-control" name="title" value={form.title} onChange={handleChange} required placeholder="Ej: Firma de escritura" />
              </div>

              <div className="row">
                {/* Tipo */}
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Tipo <span className="text-danger">*</span></label>
                    <select className="form-control" name="type" value={form.type} onChange={handleChange} required>
                      {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                </div>

                {/* Ubicación */}
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Ubicación</label>
                    <select className="form-control" name="location" value={form.location} onChange={handleChange}>
                      {LOCATION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                </div>

                {/* Responsable */}
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Responsable <span className="text-danger">*</span></label>
                    <select className="form-control" name="user_id" value={form.user_id} onChange={handleChange} required>
                      <option value="">Seleccionar...</option>
                      {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="row">
                {/* Inicio */}
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Inicio <span className="text-danger">*</span></label>
                    <input type="datetime-local" className="form-control" name="start_datetime" value={form.start_datetime} onChange={handleChange} required />
                  </div>
                </div>

                {/* Fin */}
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Fin <span className="text-danger">*</span></label>
                    <input type="datetime-local" className="form-control" name="end_datetime" value={form.end_datetime} onChange={handleChange} required />
                  </div>
                </div>
              </div>

              <div className="row">
                {/* Cliente */}
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Cliente</label>
                    <select className="form-control" name="client_id" value={form.client_id} onChange={handleChange}>
                      <option value="">Sin cliente</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                </div>

                {/* Expediente */}
                <div className="col-md-6">
                  <div className="form-group">
                    <label>Expediente</label>
                    <select className="form-control" name="case_file_id" value={form.case_file_id} onChange={handleChange} disabled={!form.client_id}>
                      <option value="">Sin expediente</option>
                      {caseFiles.map(cf => <option key={cf.id} value={cf.id}>#{cf.id} — {cf.matter}</option>)}
                    </select>
                    {!form.client_id && <small className="text-muted">Selecciona un cliente primero</small>}
                  </div>
                </div>
              </div>

              {/* Notas */}
              <div className="form-group">
                <label>Notas</label>
                <textarea className="form-control" name="notes" value={form.notes} onChange={handleChange} rows={3} placeholder="Observaciones adicionales..." />
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Crear cita'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}